import React, { useState, useEffect, useRef } from "react";
import { Typography, Button } from "antd";
import {
  ArrowRightOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";

const { Title, Paragraph } = Typography;

const WORKFLOW_STEPS = [
  {
    id: 0,
    number: "01",
    stepTag: "STEP 1 OF 3",
    title: "Create Organization & Workspace",
    shortTitle: "Workspace Setup",
    heroDesc:
      "Register your company in 60 seconds. DealVoice provisions a dedicated, multi-tenant database namespace and generates your unique Company Code for instant, secure team pairing.",
    color: "#2459d3",
    bgGradient: "linear-gradient(135deg, #3b82f6 0%, #2459d3 100%)",
    features: [
      {
        title: "Dedicated Multi-Tenant Namespace",
        desc: "Strict database-level customer data isolation (salesdb_companycode).",
      },
      {
        title: "Instant Company Code Pairing",
        desc: "Unique code (e.g. STP-1603) connects web and Android instantly.",
      },
      {
        title: "Granular Role-Based Access (RBAC)",
        desc: "Custom permissions for Superadmins, Admins, Sales Managers, and Closers.",
      },
    ],
  },
  {
    id: 1,
    number: "02",
    stepTag: "STEP 2 OF 3",
    title: "Connect Android & Import Leads",
    shortTitle: "Android & Leads",
    heroDesc:
      "Install the native Android app from Google Play and bulk import customer leads via CSV/Excel. Every sales conversation is tracked and duration-timed in the background without manual data entry.",
    color: "#2459d3",
    bgGradient: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
    features: [
      {
        title: "Zero-Touch Background Telephony",
        desc: "Runs silently to capture inbound, outbound, and duration metrics.",
      },
      {
        title: "1-Click CSV / Excel Bulk Lead Import",
        desc: "Import thousands of prospects with smart deduplication and assignment rules.",
      },
      {
        title: "Real-Time Push Notifications",
        desc: "Instant Android alarms for assigned hot leads and task follow-ups.",
      },
    ],
  },
  {
    id: 2,
    number: "03",
    stepTag: "STEP 3 OF 3",
    title: "Close Deals & Invoice Clients",
    shortTitle: "Pipeline & Invoicing",
    heroDesc:
      "Monitor real-time call telemetry curves, eliminate pipeline blindspots with automated 7-day stagnant deal warnings, and dispatch itemized GST invoices with digital Razorpay payment links.",
    color: "#2459d3",
    bgGradient: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
    features: [
      {
        title: "7-Day Stagnant Deal Warning Radar",
        desc: "Flags deals inactive past 7 days across stages before prospects go cold.",
      },
      {
        title: "Itemized GST Quotes & Invoices",
        desc: "Turn proposals into tax-compliant invoices with version control in 60 seconds.",
      },
      {
        title: "Direct Razorpay Payment Links",
        desc: "Collect cash instantly via UPI, Cards, and NetBanking.",
      },
    ],
  },
];

function StepUiPreview({ stepId }) {
  if (stepId === 0) {
    return (
      <svg className="iso-ui-svg" viewBox="0 0 140 86" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="140" height="86" rx="8" fill="currentColor" fillOpacity="0.1" stroke="currentColor" strokeWidth="1" strokeOpacity="0.25" />
        <circle cx="10" cy="9" r="2.5" fill="currentColor" fillOpacity="0.4" />
        <circle cx="17" cy="9" r="2.5" fill="currentColor" fillOpacity="0.4" />
        <circle cx="24" cy="9" r="2.5" fill="currentColor" fillOpacity="0.4" />
        <rect x="94" y="5" width="40" height="8" rx="4" fill="currentColor" fillOpacity="0.25" />
        <rect x="6" y="18" width="128" height="28" rx="6" fill="currentColor" fillOpacity="0.1" stroke="currentColor" strokeWidth="0.8" strokeOpacity="0.2" />
        <circle cx="18" cy="32" r="6" fill="currentColor" fillOpacity="0.5" />
        <rect x="28" y="27" width="48" height="4" rx="2" fill="currentColor" fillOpacity="0.8" />
        <rect x="28" y="34" width="32" height="3" rx="1.5" fill="currentColor" fillOpacity="0.45" />
        <rect x="90" y="27" width="38" height="10" rx="4" fill="currentColor" fillOpacity="0.35" />
        <rect x="6" y="51" width="128" height="28" rx="6" fill="currentColor" fillOpacity="0.08" stroke="currentColor" strokeWidth="0.8" strokeOpacity="0.18" />
        <circle cx="18" cy="65" r="6" fill="currentColor" fillOpacity="0.4" />
        <rect x="28" y="60" width="56" height="4" rx="2" fill="currentColor" fillOpacity="0.75" />
        <rect x="28" y="67" width="38" height="3" rx="1.5" fill="currentColor" fillOpacity="0.4" />
        <rect x="90" y="60" width="38" height="10" rx="4" fill="currentColor" fillOpacity="0.25" />
      </svg>
    );
  }

  if (stepId === 1) {
    return (
      <svg className="iso-ui-svg" viewBox="0 0 140 86" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="0" y="0" width="140" height="86" rx="8" fill="currentColor" fillOpacity="0.1" stroke="currentColor" strokeWidth="1" strokeOpacity="0.25" />
        <rect x="6" y="6" width="128" height="28" rx="6" fill="currentColor" fillOpacity="0.18" stroke="currentColor" strokeWidth="0.8" strokeOpacity="0.3" />
        <circle cx="18" cy="20" r="6" fill="#22c55e" fillOpacity="0.9" />
        <path d="M15.5 20L17.5 22L21 18.5" stroke="#ffffff" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="28" y="14" width="44" height="4" rx="2" fill="currentColor" fillOpacity="0.85" />
        <rect x="28" y="21" width="30" height="3" rx="1.5" fill="currentColor" fillOpacity="0.5" />
        <rect x="86" y="18" width="2.5" height="6" rx="1" fill="currentColor" fillOpacity="0.7" />
        <rect x="91" y="14" width="2.5" height="13" rx="1" fill="currentColor" fillOpacity="0.9" />
        <rect x="96" y="12" width="2.5" height="17" rx="1" fill="currentColor" fillOpacity="1" />
        <rect x="101" y="15" width="2.5" height="11" rx="1" fill="currentColor" fillOpacity="0.8" />
        <rect x="106" y="18" width="2.5" height="6" rx="1" fill="currentColor" fillOpacity="0.6" />
        <rect x="112" y="15" width="18" height="10" rx="3" fill="currentColor" fillOpacity="0.3" />
        <rect x="6" y="39" width="128" height="20" rx="4" fill="currentColor" fillOpacity="0.08" />
        <rect x="12" y="47" width="42" height="4" rx="2" fill="currentColor" fillOpacity="0.75" />
        <rect x="92" y="44" width="36" height="9" rx="3" fill="currentColor" fillOpacity="0.25" />
        <rect x="6" y="63" width="128" height="18" rx="4" fill="currentColor" fillOpacity="0.08" />
        <rect x="12" y="70" width="52" height="4" rx="2" fill="currentColor" fillOpacity="0.75" />
        <rect x="92" y="67" width="36" height="9" rx="3" fill="currentColor" fillOpacity="0.25" />
      </svg>
    );
  }

  return (
    <svg className="iso-ui-svg" viewBox="0 0 140 86" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="0" width="140" height="86" rx="8" fill="currentColor" fillOpacity="0.1" stroke="currentColor" strokeWidth="1" strokeOpacity="0.25" />
      <rect x="6" y="6" width="36" height="12" rx="3" fill="currentColor" fillOpacity="0.2" />
      <rect x="12" y="10" width="24" height="4" rx="2" fill="currentColor" fillOpacity="0.75" />
      <line x1="44" y1="12" x2="50" y2="12" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.4" />
      <rect x="52" y="6" width="36" height="12" rx="3" fill="currentColor" fillOpacity="0.2" />
      <rect x="58" y="10" width="24" height="4" rx="2" fill="currentColor" fillOpacity="0.75" />
      <line x1="90" y1="12" x2="96" y2="12" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.4" />
      <rect x="98" y="6" width="36" height="12" rx="3" fill="#22c55e" fillOpacity="0.3" stroke="#22c55e" strokeWidth="0.8" />
      <rect x="103" y="10" width="26" height="4" rx="2" fill="#22c55e" />
      <rect x="6" y="22" width="128" height="40" rx="6" fill="currentColor" fillOpacity="0.15" stroke="currentColor" strokeWidth="0.8" strokeOpacity="0.25" />
      <rect x="12" y="29" width="46" height="5" rx="2" fill="currentColor" fillOpacity="0.85" />
      <rect x="12" y="37" width="32" height="3" rx="1.5" fill="currentColor" fillOpacity="0.5" />
      <rect x="82" y="27" width="46" height="15" rx="4" fill="#22c55e" fillOpacity="0.9" />
      <rect x="88" y="32" width="34" height="4" rx="2" fill="#ffffff" />
      <line x1="12" y1="47" x2="128" y2="47" stroke="currentColor" strokeWidth="0.8" strokeOpacity="0.2" strokeDasharray="2 2" />
      <rect x="12" y="52" width="34" height="4" rx="2" fill="currentColor" fillOpacity="0.6" />
      <rect x="92" y="52" width="36" height="4" rx="2" fill="currentColor" fillOpacity="0.7" />
      <rect x="6" y="66" width="128" height="15" rx="3" fill="currentColor" fillOpacity="0.08" />
      <circle cx="15" cy="73.5" r="3" fill="#22c55e" />
      <rect x="22" y="71.5" width="54" height="4" rx="2" fill="currentColor" fillOpacity="0.7" />
    </svg>
  );
}

function HowItWorksSection({ openTrialSignup }) {
  const [activeStep, setActiveStep] = useState(0);
  const sectionRef = useRef(null);

  // ─── Native Scroll Progress Tracking inside 300vh Section ───────────────────
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          if (!sectionRef.current) {
            ticking = false;
            return;
          }

          const rect = sectionRef.current.getBoundingClientRect();
          const totalScrollDistance = sectionRef.current.offsetHeight - window.innerHeight;

          if (totalScrollDistance > 0) {
            // How far down the section has scrolled relative to viewport top
            const scrolled = -rect.top;
            const progress = Math.min(Math.max(scrolled / totalScrollDistance, 0), 1);

            let newStep = 0;
            if (progress >= 0.66) {
              newStep = 2;
            } else if (progress >= 0.33) {
              newStep = 1;
            } else {
              newStep = 0;
            }

            setActiveStep((prev) => (prev !== newStep ? newStep : prev));
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // ─── Smooth scroll to target step offset ────────────────────────────────────
  const syncStep = (index) => {
    setActiveStep(index);
    if (!sectionRef.current) return;

    const rect = sectionRef.current.getBoundingClientRect();
    const currentScrollY = window.scrollY;
    const sectionTop = currentScrollY + rect.top;
    const totalScrollDistance = sectionRef.current.offsetHeight - window.innerHeight;

    if (totalScrollDistance > 0) {
      const targetRatio = index / (WORKFLOW_STEPS.length - 1);
      const targetScrollY = sectionTop + targetRatio * totalScrollDistance;

      window.scrollTo({
        top: targetScrollY,
        behavior: "smooth",
      });
    }
  };

  const currentStep = WORKFLOW_STEPS[activeStep];

  return (
    <section
      className="workflow-pinned-section"
      id="how-it-works"
      ref={sectionRef}
    >
      <div className="workflow-sticky-viewport">
        <div className="workflow-container">
          {/* SECTION HEADER (Clean & Consistent with all sections) */}
          <div className="workflow-header-wrap">
            <span className="section-eyebrow-badge">HOW IT WORKS</span>
            <Title level={2} className="workflow-main-title">
              Up &amp; running in{" "}
              <span className="text-highlight-blue">3 simple steps</span>
            </Title>
            <Paragraph className="workflow-main-subtitle">
              Connect telephony telemetry, automate lead allocation across your sales floor, and close deals faster with zero manual drag.
            </Paragraph>
          </div>

          {/* MAIN CONTENT ROW */}
          <div className="workflow-inner-row">
            {/* ── LEFT COLUMN: Square 3D Isometric Stack with 4-5 o'clock UI preview ── */}
            <div className="workflow-left-col">
              <div className="iso-viewport">
                <div className="iso-wrapper">
                  {WORKFLOW_STEPS.map((step, index) => {
                    let stateClass = "iso-state-under";
                    if (index === activeStep) stateClass = "iso-state-active";
                    else if (index < activeStep) stateClass = "iso-state-peeled";
                    return (
                      <div
                        key={step.id}
                        className={`iso-card layer-${index + 1} ${stateClass}`}
                        onClick={() => syncStep(index)}
                      >
                        <div className="iso-surface">
                          {/* Top row: Number & Short title */}
                          <div className="iso-surface-header">
                            <span className="iso-num">{step.number}</span>
                            <span className="iso-short-label">{step.shortTitle}</span>
                          </div>

                          {/* 4 and 5 o'clock area: Page UI preview look */}
                          <div className="iso-surface-preview">
                            <StepUiPreview stepId={step.id} />
                          </div>

                          <div className="iso-shine" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ── RIGHT COLUMN: Minimal Professional Step Card ── */}
            <div className="workflow-right-col">
              <div className="workflow-detail-card">
                <div className="wf-card-content-animator" key={currentStep.id}>
                  <div className="wf-card-header">
                    <div className="wf-step-tag-pill">
                      <span>STEP {currentStep.number}</span>
                    </div>
                    <span className="wf-step-count">{currentStep.number} / 03</span>
                  </div>

                  <Title level={3} className="wf-card-title">
                    {currentStep.title}
                  </Title>

                  <Paragraph className="wf-card-desc">{currentStep.heroDesc}</Paragraph>

                  <div className="wf-feature-list">
                    {currentStep.features.map((feat, idx) => (
                      <div className="wf-feature-row" key={idx}>
                        <CheckCircleFilled className="wf-feature-check" />
                        <div className="wf-feature-text">
                          <strong className="wf-feat-title">{feat.title}</strong> —{" "}
                          <span className="wf-feat-desc">{feat.desc}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="wf-card-footer">
                  <Button
                    type="primary"
                    size="large"
                    className="wf-action-btn"
                    onClick={() => {
                      if (typeof openTrialSignup === "function") openTrialSignup("");
                    }}
                    icon={<ArrowRightOutlined />}
                    iconPosition="end"
                  >
                    Get started free
                  </Button>
                  <span className="wf-footer-note">No credit card required · 14-day free trial</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default HowItWorksSection;
